import { generalBlueDark, generalRed } from 'src/app-config';
import { generalHelpers, generalHthHelper, millisecondsOneDay } from '@monorepo/helpers';
import { typeBlock, typeCoords, typeFace, typeHTHBuilding, typeHTHHousehold, typeHTHTerritory, typeHTHFace, typeTerritoryNumber } from '@monorepo/models';

class HthHelper {
    FormatDateForExcel = (dateStr: string): string => {
        // Si es un número puro (posible serial de Excel)
        if (/^\d{4,6}$/.test(dateStr)) {
            const serial = parseInt(dateStr, 10);
            // Excel: 1 = 1/1/1900, pero hay un bug de año bisiesto, así que restamos 2 días
            const excelEpoch = new Date(1899, 11, 30);
            excelEpoch.setDate(excelEpoch.getDate() + serial);
            const day = excelEpoch.getDate().toString().padStart(2, '0');
            const month = (excelEpoch.getMonth() + 1).toString().padStart(2, '0');
            const year = excelEpoch.getFullYear().toString().slice(-2);
            return `${day}/${month}/${year}`;
        }
        // Si es un string de fecha
        const d = new Date(dateStr.replace(/-/g, '/'));
        if (!isNaN(d.getTime())) {
            const day = d.getDate().toString().padStart(2, '0');
            const month = (d.getMonth() + 1).toString().padStart(2, '0');
            const year = d.getFullYear().toString().slice(-2);
            return `${day}/${month}/${year}`;
        }
        // Si no es una fecha válida, muestra el texto original
        return dateStr;
    }
    private Cross = (o: typeCoords, a: typeCoords, b: typeCoords) => {
        return (a.lng - o.lng) * (b.lat - o.lat) - (a.lat - o.lat) * (b.lng - o.lng);
    };
    GetBlockCenter = (coordinates: typeCoords[]): typeCoords => {
        if (coordinates.length === 0) {
            return { lat: 0, lng: 0 };
        }
        const sumLat = coordinates.reduce((sum, coord) => sum + coord.lat, 0);
        const sumLng = coordinates.reduce((sum, coord) => sum + coord.lng, 0);
        return {
            lat: sumLat / coordinates.length,
            lng: sumLng / coordinates.length
        };
    };
    GetBlockCoordinates(polygons: typeHTHFace[], block: string): typeCoords[] {
        const blockPolygons = polygons.filter(p => p.block === block);
        if (blockPolygons.length === 0) {
            return [];
        }
        const allPoints: typeCoords[] = [];
        blockPolygons.forEach(polygon => {
            [polygon.coordsPoint1, polygon.coordsPoint2, polygon.coordsPoint3, polygon.coordsPoint4]
                .filter(Boolean)
                .forEach(coord => {
                    if (!allPoints.some(p => Math.abs(p.lat - coord!.lat) < 0.000001 && Math.abs(p.lng - coord!.lng) < 0.000001)) {
                        allPoints.push(coord!);
                    }
                })
            ;
        });
        if (allPoints.length < 3) {
            return [];
        }
        allPoints.sort((p1, p2) => p1.lng === p2.lng ? p1.lat - p2.lat : p1.lng - p2.lng);
        const lower: typeCoords[] = [];
        for (const p of allPoints) {
            while (lower.length >= 2 && this.Cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) {
                lower.pop();
            }
            lower.push(p);
        }
        const upper: typeCoords[] = [];
        for (let i = allPoints.length - 1; i >= 0; i--) {
            const p = allPoints[i];
            while (upper.length >= 2 && this.Cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) {
                upper.pop();
            }
            upper.push(p);
        }
        upper.pop();
        lower.pop();
        return lower.concat(upper);
    }
    GetDoorBell = (doorNumber: number, index: number, index1: number, hasContinuousNumbers: boolean, hasCharacters: boolean, numberPerLevel: number, hasNn: boolean): string => {
        if (hasContinuousNumbers) {
            if (hasCharacters) {
                return generalHthHelper.GetCharacterForNumber(index*numberPerLevel + index1 + 1, hasNn);
            }
            return (index*numberPerLevel + index1 + 1).toString();
        } else {
            if (hasCharacters) {
                return generalHthHelper.GetCharacterForNumber(doorNumber, hasNn);
            }
            return doorNumber.toString();
        }
    }
    GetFreeHouseholds = (building: typeHTHBuilding): number => {
        if (!building.households.length) {
            return 0;
        }
        const rest = building.households.filter(x => !x.isChecked).length;
        return rest;
    }
    GetComplexBuilding = (currentBuilding: typeHTHBuilding): { household: typeHTHHousehold | null; id: string; }[][] => (
        Array.from({ length: currentBuilding.numberPerLevel }, (_, door) =>
            Array.from({ length: currentBuilding.numberOfLevels }, (_, level) => ({
                id: `item-${level + door}-${new Date().getTime()}`,
                household: currentBuilding.households.find(h => h.level === level && h.doorNumber === door) || null
            })
        ))
    );
    GetLastTimeOfBuilding = (building: typeHTHBuilding): string => {
        const households = building.households.filter(h => !!h.history.filter(x => !!x.state).length);
        if (!households.length) {
            return 'Sin registros';
        }
        const dates = households.map(h => h.history.filter(x => !!x.state).map(x => x.timestamp)).flat();
        const maxDate = Math.max(...dates);
        const datetime = new Date(maxDate);
        datetime.setHours(datetime.getHours() - 3);
        const datetimeString = datetime.toISOString().replace('T', ' ').split('.')[0].slice(0, -3);
        let date = datetimeString.slice(0, 10);
        date = generalHelpers.InvertDateString(date);
        const time = datetimeString.slice(-5);
        if (generalHelpers.IsToday(maxDate)) {
            return `Hoy ${time} hs.`;
        }
        return `${date} ${time} hs.`;
    }
    GetLinkTextFor30DaysSharing = (domain: string, congregation: number, territoryNumber: typeTerritoryNumber, currentFace: typeHTHFace, currentBuilding: typeHTHBuilding): string => {
        let currentUrl = `####  *TERRITORIO ${territoryNumber}*   ####\n*Fecha: ${generalHelpers.GetCurrentLocalDate(true)}*`;
        currentUrl += "\n\n*EDIFICIO COMPARTIDO POR 30 DÍAS:*\n";
        currentUrl += `${currentFace.street} ${currentBuilding.streetNumber}${currentBuilding.streetNumber2 ? `/${currentBuilding.streetNumber2}` : ''}${currentBuilding.streetNumber3 ? `/${currentBuilding.streetNumber3}` : ''}`;
        currentUrl += `\n\n`;
        currentUrl += `${domain}/edificio/${congregation}/${territoryNumber}/${currentFace.block}/${currentFace.face}/${currentBuilding.streetNumber}`;
        currentUrl += `\n\n`;
        return currentUrl;
    }
    GetLinkTextForShareAllBuildings = (domain: string, hthTerritory: typeHTHTerritory, usingLettersForBlocks: boolean): string => {
        const separator = "#####################";
        const separator1 = "###########";
        let currentUrl = `${separator}\n####  *TERRITORIO ${hthTerritory.territoryNumber}*   ###\n${separator}\n\n*Fecha: ${generalHelpers.GetCurrentLocalDate()}*\n\n`;
        if (hthTerritory.mapUrl) {
            currentUrl += `\nMapa:\n${hthTerritory.mapUrl}\n\n`;
        }
        const doNotCalls = hthTerritory.faces.map(p =>
                p.doNotCalls.map(d => ({ ...d, street: p.street, block: p.block }))
            )
            .filter(x => x.length).flat()
            .sort((a, b) => parseInt(a.block) - parseInt(b.block))
        ;
        const facesWithBuildingsOrDnt = hthTerritory.faces.map(p => p).filter(p => p.buildings?.length || p.doNotCalls?.length);
        const blocksNumbers = [...new Set(facesWithBuildingsOrDnt.map(f => f.block))].sort((a, b) => parseInt(a) - parseInt(b));
        blocksNumbers.forEach(b => {
            currentUrl += `\n${separator1}\n *MANZANA ${generalHthHelper.MaskTheBlock(b, usingLettersForBlocks)}*\n${separator1}\n\n`;
            let streets = 'Calles: ';
            hthTerritory.faces.filter(p => p.block === b).forEach((p, i, t) =>
                i === t.length - 1
                ? streets += `${p.street}`
                : streets += `${p.street} | `
            );
            const hyphens = '---------------------------------';
            currentUrl += "```" + hyphens + "\n" + streets + "\n" + hyphens + "```\n\n";
            if (doNotCalls.some(d => d.block === b)) {
                currentUrl += "*NO TOCAR:*\n";
                doNotCalls.filter(d => d.block === b).forEach(d => {
                    currentUrl += `> ${d.street} ${d.streetNumber} ${d.doorBell ?? ''} (${d.date})\n`;
                });
                currentUrl += "\n";
            } else {
                currentUrl += "~No hay No Tocar en esta manzana~\n\n";
            }
            if (hthTerritory.faces.some(f => f.block === b && f.buildings?.length)) {
                currentUrl += "\n*EDIFICIOS:*\n";
                const faces = hthTerritory.faces.filter(p => p.block === b);
                faces.forEach(f => {
                    f.buildings?.sort((a, b) => a.streetNumber - b.streetNumber).forEach(b => {
                        currentUrl += `- ${f.street} ${b.streetNumber}${b.streetNumber2 ? `/${b.streetNumber2}` : ''}${b.streetNumber3 ? `/${b.streetNumber3}` : ''}`;
                        currentUrl += `\n`;
                        currentUrl += `> ${domain}/edificio/${hthTerritory.congregation}/${hthTerritory.territoryNumber}/${faces[0].block}/${f.face}/${b.streetNumber}`;
                        currentUrl += `\n\n`;
                    });
                });
            } else {
                currentUrl += "\n~No hay Edificios en esta manzana~\n\n";
            }
        });
        return currentUrl;
    }
    GetLinkTextForShareBlockBuildings = (domain: string, congregation: number, territoryNumber: typeTerritoryNumber, faces: typeHTHFace[], usingLettersForBlocks: boolean): string => {
        let text = `Territorio ${territoryNumber} - Manzana ${generalHthHelper.MaskTheBlock(faces[0].block, usingLettersForBlocks)}`;
        text += `\n\n`;
        faces.forEach(f =>
            f.buildings?.sort((a, b) => a.streetNumber - b.streetNumber).forEach(b => {
                text += `${f.street} ${b.streetNumber}`;
                text += `\n`;
                text += `${domain}/edificio/${congregation}/${territoryNumber}/${faces[0].block}/${f.face}/${b.streetNumber}`;
                text += `\n\n`;
            })
        );
        return text;
    }
    GetLinkTextForShareBlockBuildingsUnique = (domain: string, congregation: number, congregationName: string, territoryNumber: typeTerritoryNumber, faces: typeHTHFace[], usingLettersForBlocks: boolean): string => {
        let text = `*Congregación ${congregationName}*`;
        text += `\n\n`;
        text += `Acceso al *Territorio ${territoryNumber} Manzana ${generalHthHelper.MaskTheBlock(faces[0].block, usingLettersForBlocks)}* para el día ${generalHelpers.GetCurrentLocalDate()}:`;
        text += `\n\n`;
        text += '`';
        text += `${domain}/buscador/${congregation}/${territoryNumber}/${faces[0].block}`;
        text += '`';
        text += `\n\n`;
        text += `Calles: ${faces.map(f => f.street).join(' | ')}`;
        return text;
    }
    GetLinkTextForShareFaceBuildings = (domain: string, congregation: number, territoryNumber: typeTerritoryNumber, block: typeBlock, face: typeFace, street: string, buildings: number[]): string => {
        let text = '\n\n';
        for (const streetNumber of buildings) {
            text += `Edificio ${street} ${streetNumber} (Territorio ${territoryNumber})`;
            text += `\n\n`;
            text += `${domain}/edificio/${congregation}/${territoryNumber}/${block}/${face}/${streetNumber}`;
            text += `\n\n`;
        }
        return text;
    }
    GetNewComplexBuilding = (numberOfLevels: number, numberPerLevel: number): { id: string; label: string; }[][] => (
        Array.from({ length: numberPerLevel }, (_, door) => (
            Array.from({ length: numberOfLevels }, (_, level) => ({
                id: `item-${level + door}-${new Date().getTime()}`,
                label: `Fila ${level + 1} Columna ${door + 1}`
            }))
        ))
    );
    GetPolygonCenter = (blockCoordinates: typeCoords[]): typeCoords => {
        let sumLat = 0;
        let sumLng = 0;
        for (const coordinate of blockCoordinates) {
            sumLat += coordinate.lat;
            sumLng += coordinate.lng;
        }
        const lat = sumLat / blockCoordinates.length;
        const lng = sumLng / blockCoordinates.length;
        return { lat, lng };
    }
    GetPolygonCoordinates = (numberOfCoordinate: number, numberOfPolygon: number, blockCoordinates: typeCoords[]): typeCoords => {
        if (numberOfCoordinate === 3) {
            const center: typeCoords = this.GetPolygonCenter(blockCoordinates);
            return center;
        }
        const coordinate: typeCoords = {
            lat: 0,
            lng: 0
        };
        if (numberOfPolygon === 1) {
            if (numberOfCoordinate === 1) {
                coordinate.lat = blockCoordinates[0].lat;
                coordinate.lng = blockCoordinates[0].lng;
            } else if (numberOfCoordinate === 2) {
                coordinate.lat = blockCoordinates[1].lat;
                coordinate.lng = blockCoordinates[1].lng;
            }
        } else if (numberOfPolygon === 2) {
            if (numberOfCoordinate === 1) {
                coordinate.lat = blockCoordinates[1].lat;
                coordinate.lng = blockCoordinates[1].lng;
            } else if (numberOfCoordinate === 2) {
                coordinate.lat = blockCoordinates[2].lat;
                coordinate.lng = blockCoordinates[2].lng;
            }
        } else if (numberOfPolygon === 3) {
            if (numberOfCoordinate === 1) {
                coordinate.lat = blockCoordinates[2].lat;
                coordinate.lng = blockCoordinates[2].lng;
            } else if (numberOfCoordinate === 2) {
                coordinate.lat = blockCoordinates[3].lat;
                coordinate.lng = blockCoordinates[3].lng;
            }
        } else if (numberOfPolygon === 4) {
            if (numberOfCoordinate === 1) {
                coordinate.lat = blockCoordinates[3].lat;
                coordinate.lng = blockCoordinates[3].lng;
            } else if (numberOfCoordinate === 2) {
                coordinate.lat = blockCoordinates[0].lat;
                coordinate.lng = blockCoordinates[0].lng;
            }
        }
        return coordinate;
    }
    GetStreetsByTerritory = (hthTerritory: typeHTHTerritory): string[] => {
        if (!hthTerritory || !hthTerritory.faces) {
            return [];
        }
        const streets: string[] = [];
        hthTerritory.faces.forEach(x => {
            if (x.street && !streets.includes(x.street)) {
                streets.push(x.street);
            }
        });
        return streets;
    }
    IsAllowedToChangeState = (household: typeHTHHousehold) => {
        return !!household.history
         .filter(h => !!h.state)
         .map(h => h.timestamp)
         .some(x => generalHelpers.IsToday(x) && Date.now() - x < millisecondsOneDay);
    }
    SortCoordinatesClockwise = (coordinates: typeCoords[]): typeCoords[] => {
        const center = this.GetPolygonCenter(coordinates);
        const sorted = coordinates.slice().sort((p1, p2) => {
            const angleA = Math.atan2(p1.lat - center.lat, p1.lng - center.lng);
            const angleB = Math.atan2(p2.lat - center.lat, p2.lng - center.lng);
            return angleB - angleA;
        });
        const distances = sorted.map(p => ({
            point: p,
            distance: Math.abs(p.lat) + Math.abs(p.lng)
        }));
        distances.sort((d1, d2) => d1.distance - d2.distance);
        const closestPoint = distances[0].point;
        const index = sorted.findIndex(p => p === closestPoint);
        const rotated = [...sorted.slice(index), ...sorted.slice(0, index)];
        return rotated;
    }
    SetIsCoveredAndColorForHTHPolygons = (hthTerritories: typeHTHTerritory[], serviceId: number, completionDate: number | null): typeHTHTerritory[] => {
        return hthTerritories.map(t => ({
            ...t,
            polygons: t.faces.map(p => {
                const activity = p.serviceDates.find(s => s.serviceId === serviceId);
                return ({
                        ...p,
                        color: activity ?
                            activity.completionDate === completionDate ? generalBlueDark : generalRed
                            :
                            'lightgrey',
                        isCovered: p.serviceDates.some(s => s.serviceId === serviceId && (!completionDate || s.completionDate === completionDate))
                    });
                })
        }));
    }
}

export const hthHelper = new HthHelper();
